import m from "mithril";
import { getMithril, postMithril, showToast, getSyncUserData } from "../../../utils.js";

const CALK = {
    loading: false,
    mode: "list", // 'list', 'form', 'view'

    reports: [],

    // Form State
    form: {
        id: null,
        reportNo: "",
        reportDate: new Date().toISOString().split('T')[0],
        reportYear: new Date().getFullYear(),

        // 1. Umum
        entityName: "",
        entityDomicile: "",
        entityOperationsNature: "Koperasi Simpan Pinjam",

        // 2. Pernyataan Kepatuhan
        complianceStatement: "",

        // 3. Kebijakan Akuntansi
        accountingPolicies: "",

        // 4. Catatan Rincian
        detailNotes: "",

        // 5. Informasi Tambahan
        additionalInfo: ""
    },

    oninit: function () {
        this.fetchList();
    },

    fetchList: async function () {
        this.loading = true;
        m.redraw();

        try {
            const result = await getMithril('/api/accounting/reports/calk', { component: this });
            if (result.success) {
                this.reports = result.data || [];
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    loadReport: async function (id) {
        this.loading = true;
        m.redraw();
        try {
            const result = await getMithril(`/api/accounting/reports/calk?id=${id}`, { component: this });
            if (result.success && result.data) {
                const data = result.data;
                this.form = {
                    id: data.id,
                    reportNo: data.reportNo,
                    reportDate: data.reportDate.split('T')[0],
                    reportYear: data.reportYear,
                    entityName: data.entityName,
                    entityDomicile: data.entityDomicile,
                    entityOperationsNature: data.entityOperationsNature,
                    complianceStatement: data.complianceStatement || "",
                    accountingPolicies: data.accountingPolicies || "",
                    detailNotes: data.detailNotes || "",
                    additionalInfo: data.additionalInfo || ""
                };
                this.mode = "form";
            }
        } catch (e) {
            console.error(e);
            showToast("Gagal memuat dokumen", "error");
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    createNew: async function () {
        this.loading = true;
        this.mode = "form";
        m.redraw();

        // Reset form
        this.form = {
            id: null,
            reportNo: `CALK/001/${new Date().getFullYear()}`,
            reportDate: new Date().toISOString().split('T')[0],
            reportYear: new Date().getFullYear(),

            entityName: "",
            entityDomicile: "",
            entityOperationsNature: "Koperasi Simpan Pinjam",
            complianceStatement: "",
            accountingPolicies: "",
            detailNotes: "",
            additionalInfo: ""
        };

        // Attempt to auto fill from subunits
        try {
            const user = getSyncUserData();
            const subunitsRes = await getMithril('/api/subunits', { showErrorToast: false });
            if (subunitsRes.success && subunitsRes.data) {
                const mySubunit = subunitsRes.data.find(s => s.id == user.subunitId);
                if (mySubunit) {
                    console.log("🔧 CALK DEBUG - Subunit found:", mySubunit);
                    this.form.entityName = mySubunit.nama || mySubunit.name || "";

                    // Fetch full location hierarchy from indoarea
                    const pCode = mySubunit.provinceId || mySubunit.kode_provinsi || mySubunit.provinceCode;
                    const rCode = mySubunit.regencyId || mySubunit.kode_kabkota || mySubunit.regencyCode;
                    const dCode = mySubunit.districtId || mySubunit.kode_kecamatan || mySubunit.districtCode;
                    const vCode = mySubunit.villageId || mySubunit.kode_kelurahan || mySubunit.villageCode;

                    console.log("🔧 CALK DEBUG - Codes identified:", { pCode, rCode, dCode, vCode });

                    const fetchInList = async (url, code, prefix = "") => {
                        try {
                            const res = await fetch(url).then(r => r.json());
                            if (res.data && res.data.length > 0) {
                                // Find exactly matching code or fullCode
                                const item = res.data.find(i => i.code === code || i.fullCode === code);
                                if (item) return (prefix ? prefix + " " : "") + item.name;
                                // Fallback to first if only one item returned (some custom endpoints)
                                if (res.data.length === 1 && !code) return (prefix ? prefix + " " : "") + res.data[0].name;
                            }
                        } catch (e) { console.error(`Failed fetching from ${url}`, e); }
                        return null;
                    };

                    const results = await Promise.all([
                        vCode ? fetchInList(`https://indoarea.vercel.app/api/kelurahan-desa?kecamatan_code=${dCode}`, vCode, "Kel/Desa") : Promise.resolve(null),
                        dCode ? fetchInList(`https://indoarea.vercel.app/api/kecamatan?kabkota_code=${rCode}`, dCode, "Kec.") : Promise.resolve(null),
                        rCode ? fetchInList(`https://indoarea.vercel.app/api/kabupaten-kota?provinsi_code=${pCode}`, rCode) : Promise.resolve(null),
                        pCode ? fetchInList(`https://indoarea.vercel.app/api/provinsi`, pCode, "Prov.") : Promise.resolve(null)
                    ]);

                    const parts = results.filter(n => n !== null);

                    if (parts.length > 0) {
                        const mainAddress = mySubunit.streetAddress || mySubunit.address || "";
                        this.form.entityDomicile = (mainAddress ? mainAddress + ", " : "") + parts.join(', ');
                        console.log("🔧 CALK DEBUG - Final Domicile:", this.form.entityDomicile);
                    } else {
                        this.form.entityDomicile = mySubunit.streetAddress || mySubunit.address || "";
                        console.log("🔧 CALK DEBUG - No location names could be resolved. Using streetAddress fallback.");
                    }
                } else {
                    console.log("🔧 CALK DEBUG - Subunit not found for ID:", user.subunitId);
                }
            }
        } catch (e) {
            console.log('Error autofilling subunit data', e);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    saveReport: async function (e) {
        e.preventDefault();
        this.loading = true;
        m.redraw();

        try {
            const result = await postMithril('/api/accounting/reports/calk', this.form, { component: this });
            if (result.success) {
                showToast(result.message, "success");
                this.mode = "list";
                this.fetchList();
            } else {
                showToast(result.message || "Gagal menyimpan", "error");
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.loading = false;
            m.redraw();
        }
    },

    renderList: function () {
        return m("div", { class: "animate-fade-in" }, [
            m("div", { class: "flex justify-between items-center mb-6" }, [
                m("div", [
                    m("h1", { class: "text-2xl font-bold text-gray-900" }, "Catatan Atas Laporan Keuangan (CALK)"),
                    m("p", { class: "text-sm text-gray-500 mt-1" }, "Sesuai standar SAK Entitas Privat (SAK EP)")
                ]),
                m("button", {
                    class: "px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg shadow-sm transition-colors flex items-center gap-2",
                    onclick: () => this.createNew()
                }, [
                    m("i", { class: "fas fa-plus" }),
                    "Buat Laporan Baru"
                ])
            ]),

            m("div", { class: "bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden" }, [
                m("table", { class: "min-w-full divide-y divide-gray-200" }, [
                    m("thead", { class: "bg-gray-50" }, [
                        m("tr", [
                            m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Nomor & Tahun"),
                            m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Tanggal"),
                            m("th", { class: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Nama Entitas"),
                            m("th", { class: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" }, "Aksi")
                        ])
                    ]),
                    m("tbody", { class: "bg-white divide-y divide-gray-200" }, [
                        this.reports.length === 0
                            ? m("tr", [
                                m("td", { colspan: "4", class: "px-6 py-8 text-center text-sm text-gray-500" }, "Belum ada data CALK tersimpan.")
                            ])
                            : this.reports.map(report =>
                                m("tr", { class: "hover:bg-gray-50 transition-colors" }, [
                                    m("td", { class: "px-6 py-4" }, [
                                        m("div", { class: "text-sm font-medium text-gray-900" }, report.reportNo),
                                        m("div", { class: "text-xs text-gray-500" }, `Tahun Baku: ${report.reportYear}`)
                                    ]),
                                    m("td", { class: "px-6 py-4" }, [
                                        m("div", { class: "text-sm text-gray-900" }, new Date(report.reportDate).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }))
                                    ]),
                                    m("td", { class: "px-6 py-4 text-sm text-gray-500" }, report.entityName),
                                    m("td", { class: "px-6 py-4 text-right text-sm font-medium space-x-3" }, [
                                        m("button", {
                                            class: "text-indigo-600 hover:text-indigo-900",
                                            onclick: () => this.loadReport(report.id)
                                        }, "Edit & View")
                                    ])
                                ])
                            )
                    ])
                ])
            ])
        ]);
    },

    renderForm: function () {
        return m("div", { class: "animate-fade-in pb-20" }, [
            m("div", { class: "flex items-center gap-4 mb-6" }, [
                m("button", {
                    class: "p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors",
                    onclick: () => this.mode = "list"
                }, m("i", { class: "fas fa-arrow-left" })),
                m("h1", { class: "text-2xl font-bold text-gray-900" }, this.form.id ? "Edit CALK" : "Buat CALK Baru"),
                this.form.id && m("button", {
                    class: "ml-auto px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm text-sm font-medium transition-colors flex items-center gap-2",
                    onclick: () => this.mode = "view"
                }, [m("i", { class: "fas fa-print" }), "Preview Dokumen"])
            ]),

            m("form", { onsubmit: (e) => this.saveReport(e), class: "space-y-8" }, [

                // Section: Identitas Laporan
                m("div", { class: "bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4" }, [
                    m("h3", { class: "text-lg font-bold text-gray-900 border-b pb-2 mb-4" }, "A. Identitas Laporan"),
                    m("div", { class: "grid grid-cols-1 md:grid-cols-3 gap-6" }, [
                        m("div", [
                            m("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "Nomor Laporan"),
                            m("input", {
                                type: "text", required: true,
                                class: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500",
                                value: this.form.reportNo,
                                oninput: e => this.form.reportNo = e.target.value
                            })
                        ]),
                        m("div", [
                            m("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "Tanggal Cetak"),
                            m("input", {
                                type: "date", required: true,
                                class: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500",
                                value: this.form.reportDate,
                                oninput: e => this.form.reportDate = e.target.value
                            })
                        ]),
                        m("div", [
                            m("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "Tahun Pembukuan"),
                            m("input", {
                                type: "number", required: true,
                                class: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500",
                                value: this.form.reportYear,
                                oninput: e => this.form.reportYear = e.target.value
                            })
                        ])
                    ])
                ]),

                // Section 1: Umum
                m("div", { class: "bg-white p-6 rounded-xl shadow-sm border border-gray-200 space-y-4" }, [
                    m("h3", { class: "text-lg font-bold text-gray-900 border-b pb-2 mb-4" }, "1. Umum (Deskripsi Entitas)"),
                    m("div", { class: "grid grid-cols-1 md:grid-cols-3 gap-6" }, [
                        m("div", [
                            m("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "Nama Entitas"),
                            m("input", {
                                type: "text", required: true,
                                class: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500",
                                value: this.form.entityName,
                                oninput: e => this.form.entityName = e.target.value
                            })
                        ]),
                        m("div", [
                            m("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "Domisili (Kota/Kab)"),
                            m("input", {
                                type: "text", required: true,
                                class: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500",
                                value: this.form.entityDomicile,
                                oninput: e => this.form.entityDomicile = e.target.value
                            })
                        ]),
                        m("div", [
                            m("label", { class: "block text-sm font-medium text-gray-700 mb-1" }, "Sifat Operasi"),
                            m("input", {
                                type: "text", required: true,
                                class: "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500",
                                value: this.form.entityOperationsNature,
                                oninput: e => this.form.entityOperationsNature = e.target.value
                            })
                        ])
                    ])
                ]),

                // Section 2: Pernyataan Kepatuhan
                m("div", { class: "bg-white p-6 rounded-xl shadow-sm border border-gray-200" }, [
                    m("h3", { class: "text-lg font-bold text-gray-900 border-b pb-2 mb-4" }, "2. Pernyataan Kepatuhan dan Dasar Penyusunan"),
                    m("textarea", {
                        class: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[120px]",
                        placeholder: "Tuliskan pernyataan eksplisit bahwa laporan keuangan disusun sesuai dengan SAK Entitas Privat (SAK EP). Sebutkan dasar pengukuran (misal: biaya historis) dan mata uang fungsional yang digunakan.",
                        value: this.form.complianceStatement,
                        oninput: e => this.form.complianceStatement = e.target.value
                    })
                ]),

                // Section 3: Kebijakan Akuntansi
                m("div", { class: "bg-white p-6 rounded-xl shadow-sm border border-gray-200" }, [
                    m("h3", { class: "text-lg font-bold text-gray-900 border-b pb-2 mb-4" }, "3. Kebijakan Akuntansi"),
                    m("textarea", {
                        class: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[160px]",
                        placeholder: "Uraikan kebijakan akuntansi yang diterapkan, seperti metode pengakuan pendapatan, penilaian persediaan, penyusutan aset tetap, dan pengakuan imbalan kerja. Jelaskan jika ada perubahan kebijakan akuntansi dari periode sebelumnya.",
                        value: this.form.accountingPolicies,
                        oninput: e => this.form.accountingPolicies = e.target.value
                    })
                ]),

                // Section 4: Catatan Rincian
                m("div", { class: "bg-white p-6 rounded-xl shadow-sm border border-gray-200" }, [
                    m("h3", { class: "text-lg font-bold text-gray-900 border-b pb-2 mb-4" }, "4. Catatan Rincian"),
                    m("textarea", {
                        class: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[250px] font-mono text-sm",
                        placeholder: "Isikan angka-angka pendukung, diberi referensi silang ke neraca/laporan perhitungan hasil usaha. Berikan rincian lebih lanjut untuk setiap pos.\nContoh:\n- Rincian kas dan setara kas\n- Umur piutang\n- Rincian aset tetap (harga perolehan, akumulasi penyusutan, nilai buku)",
                        value: this.form.detailNotes,
                        oninput: e => this.form.detailNotes = e.target.value
                    })
                ]),

                // Section 5: Informasi Tambahan
                m("div", { class: "bg-white p-6 rounded-xl shadow-sm border border-gray-200" }, [
                    m("h3", { class: "text-lg font-bold text-gray-900 border-b pb-2 mb-4" }, "5. Informasi Tambahan"),
                    m("textarea", {
                        class: "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[120px]",
                        placeholder: "Berisi pengungkapan lain yang relevan, misal: Informasi mengenai komitmen, kontinjensi (perkara hukum), atau transaksi dengan pihak berelasi dan/atau penjelasan mengenai estimasi dan asumsi yang digunakan.",
                        value: this.form.additionalInfo,
                        oninput: e => this.form.additionalInfo = e.target.value
                    })
                ]),

                m("div", { class: "flex justify-end gap-3 sticky bottom-4 z-10 p-4 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-gray-200" }, [
                    m("button", {
                        type: "button",
                        class: "px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors",
                        onclick: () => this.mode = "list"
                    }, "Batal"),
                    m("button", {
                        type: "submit",
                        class: "px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm",
                        disabled: this.loading
                    }, this.loading ? "Menyimpan..." : "Simpan Laporan")
                ])
            ])
        ]);
    },

    renderDocument: function () {
        // Formatter to render line breaks properly
        const formatText = (text) => {
            if (!text) return m("span", { class: "text-gray-400 italic" }, "Tidak ada pengungkapan.");
            return text.split('\n').map(item => m("span", [item, m("br")]));
        };

        return m("div", { class: "min-h-screen bg-gray-100 py-8 px-4 animate-fade-in" }, [

            // Toolbar
            m("div", { class: "max-w-4xl mx-auto mb-6 flex justify-between items-center print:hidden" }, [
                m("button", {
                    class: "px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2",
                    onclick: () => this.mode = "form"
                }, [m("i", { class: "fas fa-arrow-left" }), "Kembali ke Editor"]),

                m("button", {
                    class: "px-4 py-2 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center gap-2",
                    onclick: () => window.print()
                }, [m("i", { class: "fas fa-print" }), "Cetak Dokumen"])
            ]),

            // Document A4 Sheet
            m("div", { class: "max-w-4xl mx-auto bg-white p-12 md:p-20 shadow-xl print:shadow-none print:p-0" }, [

                // Header
                m("div", { class: "text-center mb-12 border-b-2 border-black pb-8" }, [
                    m("h1", { class: "text-2xl font-bold uppercase tracking-wider mb-2" }, this.form.entityName),
                    m("h2", { class: "text-xl font-bold uppercase tracking-wide" }, "CATATAN ATAS LAPORAN KEUANGAN (CALK)"),
                    m("p", { class: "text-gray-700 mt-2" }, `Untuk Tahun yang Berakhir pada 31 Desember ${this.form.reportYear}`),
                    m("p", { class: "text-gray-700 font-medium" }, `Nomor: ${this.form.reportNo}`)
                ]),

                // Content Sections
                m("div", { class: "space-y-8 text-gray-900 text-justify leading-relaxed" }, [

                    m("section", [
                        m("h3", { class: "font-bold text-lg mb-2" }, "1. UMUM"),
                        m("p", { class: "mb-2" }, [
                            m("strong", "Nama Entitas: "), this.form.entityName
                        ]),
                        m("p", { class: "mb-2" }, [
                            m("strong", "Domisili: "), this.form.entityDomicile
                        ]),
                        m("p", { class: "mb-2" }, [
                            m("strong", "Sifat Operasi: "), this.form.entityOperationsNature
                        ])
                    ]),

                    m("section", [
                        m("h3", { class: "font-bold text-lg mb-2" }, "2. PERNYATAAN KEPATUHAN DAN DASAR PENYUSUNAN"),
                        m("div", { class: "pl-4" }, formatText(this.form.complianceStatement))
                    ]),

                    m("section", [
                        m("h3", { class: "font-bold text-lg mb-2" }, "3. KEBIJAKAN AKUNTANSI"),
                        m("div", { class: "pl-4" }, formatText(this.form.accountingPolicies))
                    ]),

                    m("section", [
                        m("h3", { class: "font-bold text-lg mb-2" }, "4. CATATAN RINCIAN"),
                        m("div", { class: "pl-4 font-mono text-sm whitespace-pre-wrap" }, this.form.detailNotes || "Tidak ada rincian.")
                    ]),

                    m("section", [
                        m("h3", { class: "font-bold text-lg mb-2" }, "5. INFORMASI TAMBAHAN"),
                        m("div", { class: "pl-4" }, formatText(this.form.additionalInfo))
                    ])

                ]),

                // Footer / Signatures
                m("div", { class: "mt-24 pt-8 grid grid-cols-2 gap-8 text-center" }, [
                    m("div", [
                        m("p", { class: "mb-20" }, "Mengetahui,"),
                        m("p", { class: "font-bold border-b border-black inline-block px-8" }, "PENGURUS / MANAJER")
                    ]),
                    m("div", [
                        m("p", { class: "mb-20" }, `${this.form.entityDomicile}, ${new Date(this.form.reportDate).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`),
                        m("p", { class: "font-bold border-b border-black inline-block px-8" }, "PEMBUAT LAPORAN")
                    ])
                ])

            ])
        ]);
    },

    view: function () {
        return m("div", { class: "container mx-auto" }, [
            this.mode === "list" && this.renderList(),
            this.mode === "form" && this.renderForm(),
            this.mode === "view" && this.renderDocument(),

            // Global standard Loading Overlay
            (this.loading && this.mode !== "list") && m("div", { class: "fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center" }, [
                m("div", { class: "animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" })
            ])
        ]);
    }
};

export default CALK;
